import { Link } from "react-router-dom";
import { Phone, MessageCircle, ClipboardCheck, AlertTriangle, Clock, Radio } from "lucide-react";
import { StageBadge, StatusBadge, ServiceTag } from "@/components/Badges";
import { useCrm } from "@/context/CrmContext";
import { fmtTime, waLink } from "@/lib/constants";

const urgency = (f) =>
  f.pending_disposition ? "border-l-rose-500" : f.in_progress ? "border-l-emerald-500" : f.overdue ? "border-l-amber-500" : f.due_today ? "border-l-[#1F9E7A]" : "border-l-neutral-200";

export const LeadCard = ({ lead, index = 0 }) => {
  const { logAndOpen, openDisposition } = useCrm();
  const f = lead.flags || {};
  const meeting = lead.pending_meeting || lead.live_meeting;
  return (
    <div data-testid={`lead-card-${lead.id}`} style={{ animationDelay: `${index * 40}ms` }}
      className={`fade-up rounded-2xl border border-neutral-200 border-l-4 ${urgency(f)} bg-white p-4 transition-colors duration-150 hover:bg-[#F6F8F5]`}>
      <Link to={`/leads/${lead.id}`} data-testid={`lead-card-open-${lead.id}`} className="block">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display truncate text-base font-semibold text-[#1A1A1A]">{lead.full_name}</h3>
            <p className="truncate text-sm text-neutral-500">{lead.company_name || "Individual"}</p>
          </div>
          <StageBadge stage={lead.lifecycle_stage} />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5"><ServiceTag service={lead.service_category} /><StatusBadge status={lead.lead_status} /></div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          {f.pending_disposition ? (
            <span className="flex items-center gap-1 font-semibold text-rose-700"><AlertTriangle className="h-3.5 w-3.5" />Meeting outcome pending</span>
          ) : f.in_progress ? (
            <span className="flex items-center gap-1 font-semibold text-emerald-700"><Radio className="h-3.5 w-3.5" />Meeting in progress</span>
          ) : (
            <span className={`flex items-center gap-1 font-mono ${f.overdue ? "text-amber-700" : "text-neutral-500"}`}>
              <Clock className="h-3.5 w-3.5" />{f.overdue ? "Overdue · " : ""}{fmtTime(lead.action_at || lead.next_action_at)}
            </span>
          )}
          {lead.assigned_user_name && <span className="ml-auto truncate text-neutral-400">{lead.assigned_user_name}</span>}
        </div>
      </Link>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <a href={`tel:${lead.phone_e164}`} data-testid={`lead-card-action-call-${lead.id}`} onClick={() => logAndOpen(lead, "CALL")}
          className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#1F9E7A] text-sm font-semibold text-white transition-colors hover:bg-[#178566]">
          <Phone className="h-4 w-4" />Call
        </a>
        <a href={waLink(lead.phone_e164, `Hi ${lead.full_name}, this is Saminfratech.`)} target="_blank" rel="noreferrer"
          data-testid={`lead-card-action-whatsapp-${lead.id}`} onClick={() => logAndOpen(lead, "WHATSAPP")}
          className="flex h-11 items-center justify-center gap-1.5 rounded-lg bg-[#25D366] text-sm font-semibold text-[#06281a] transition-colors hover:bg-[#1fb857]">
          <MessageCircle className="h-4 w-4" />WhatsApp
        </a>
        <button type="button" data-testid={`lead-card-action-log-${lead.id}`} onClick={() => openDisposition(lead, meeting)}
          className={`flex h-11 items-center justify-center gap-1.5 rounded-lg border text-sm font-semibold transition-colors ${meeting ? "border-rose-300 bg-rose-50 text-rose-700" : "border-neutral-200 bg-white text-neutral-800 hover:border-neutral-400"}`}>
          <ClipboardCheck className="h-4 w-4" />Log
        </button>
      </div>
    </div>
  );
};
